import { useTranslation } from 'next-i18next';
import React from 'react';
import styled from 'styled-components';
import { BsButtonWithIcon } from '@hotelplan/core.fusion.bs-button-with-icon';
import { formatPrice } from '@hotelplan/fdr.lib.format.format-price';
import { FdrImage } from '@hotelplan/fdr.regular.basis.fdr-image';
import { E_BUTTON_TYPE } from '@hotelplan/style.hotelplan-style';
import { sx2CssThemeFn } from '@hotelplan/util.theme.sxc';
import useAddOfferToActiveWishlist from 'fdr/components/candidate/fdr-wishlist/fdr-use-add-offer-to-active-wishlist';
import { useWishlistSelectedItemsContext } from 'fdr/components/candidate/fdr-wishlist/fdr-wishlist-selected-items-context';
import { FdrWishlistItemFragment } from 'fdr/schemas/fragment/wishlist/fdr-wishlist.generated';

const ChangesBar = styled.div(
  ({ theme: { colors, FONT_SIZE, radii, space } }) =>
    sx2CssThemeFn({
      ...FONT_SIZE.LARGE_BOLD,
      position: 'relative',
      display: 'flex',
      justifyContent: 'space-between',
      background: colors.bgWarning,
      color: colors.black,
      borderRadius: radii.roundedTop,
      padding: [space[2], `${space[2]} ${space[3]}`],
      marginBottom: '-3px',
      zIndex: 3,
      width: '100%',
      border: '1px solid',
      borderBottom: 'none',
      borderColor: colors.lightGreyFirst,
    })
);

const ChangePriceButton = styled(BsButtonWithIcon)({
  backgroundColor: 'transparent',
});

const ImageWrapper = styled(FdrImage)({
  width: '100%',
  height: '20px',
  margin: '12px 0px',
  verticalAlign: 'middle',
});

export const Loader: React.FC = () => {
  return <ImageWrapper src="/images/spinner-button.svg" />;
};

interface IWishlistItemPriceChangesDisrupterProps {
  className?: string;
  item: FdrWishlistItemFragment;
}

const FdrWishlistItemPriceChangesDisrupter: React.FC<IWishlistItemPriceChangesDisrupterProps> =
  ({ className, item }) => {
    const [t] = useTranslation();
    const { updateItem } = useWishlistSelectedItemsContext();
    const [addOffer, loading] = useAddOfferToActiveWishlist();

    const onAcceptChanges = async () => {
      if (loading || !item.trackedOffer?.latest) return;

      const offer = await addOffer(item.trackedOffer.latest.id);

      if (!offer) return;

      updateItem({ ...offer });
    };

    return (
      <ChangesBar className={className}>
        {loading ? (
          <Loader />
        ) : (
          <>
            <div>
              {t('wishlist:overlay.priceChanged.title', {
                price: formatPrice(item.trackedOffer.latest?.priceTotal),
              })}
            </div>
            <ChangePriceButton
              data-id="change-price-button"
              icon={{ name: 'close', size: 'md' }}
              role="button"
              variant={E_BUTTON_TYPE.ICON_BUTTON}
              onClick={onAcceptChanges}
            />
          </>
        )}
      </ChangesBar>
    );
  };

export default FdrWishlistItemPriceChangesDisrupter;
